import { useMemo, useState } from 'react';
import { usePagination } from '@/hooks/usePagination';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  UserPlus,
  CheckCircle,
  Clock,
  AlertCircle,
  Play,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  EyeOff,
  Copy,
  Mail,
  Send,
  ShieldCheck,
  CalendarClock,
  Laptop,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { OnboardingPipeline, PipelineLegend, hasPipelineError } from '@/components/users/OnboardingPipeline';
import { OnboardingDetailDialog, type OnboardingRow } from '@/components/users/OnboardingDetailDialog';
import { EmailPreviewDialog } from '@/components/users/EmailPreviewDialog';
import { formatDayMonth, getCurrentWeekRangeAR, isoDateInRange } from '@/../../shared/dates';

function PasswordCell({ password }: { password: string }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  if (!password) return <span className="text-muted-foreground">-</span>;

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <span className="font-mono text-xs">{visible ? password : '••••••••'}</span>
      <Button variant="ghost" size="icon" aria-label={t('common.a11y.toggle_visibility')} className="h-6 w-6" onClick={() => setVisible(!visible)}>
        {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={() => {
          navigator.clipboard.writeText(password);
          toast.success(t('users.onboarding_page.password_copied'));
        }}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/** Equipo del inventario matcheado al ingreso por el mail (owner). */
function AssignedDeviceCell({ correo }: { correo: string }) {
  const { t } = useTranslation();
  const { data: devices, isLoading } = trpc.inventory.getDevicesByOwner.useQuery(correo, {
    enabled: !!correo,
    staleTime: 5 * 60 * 1000,
  });
  if (!correo) return <span className="text-xs text-muted-foreground opacity-50">—</span>;
  if (isLoading) return <span className="text-xs text-muted-foreground">…</span>;
  const relevant = (devices ?? []).filter((d) =>
    ['lista para entregar', 'entregado'].includes((d.status || '').trim().toLowerCase())
  );
  if (relevant.length === 0) {
    return <span className="text-xs text-muted-foreground">{t('admin.useronboarding.sin_equipo_asignado')}</span>;
  }
  return (
    <div className="space-y-0.5">
      {relevant.map((d) => (
        <div key={d.deviceId} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <Laptop className="h-3 w-3 text-muted-foreground" />
          <span>{d.modelo}</span>
          <span className={`font-medium ${d.status === 'Entregado' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
            {d.status}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Next automatic run: daily 8:00 AM Argentina (UTC-3, no DST) */
function nextRunLabel(t: (k: string, o?: any) => string): string {
  const now = new Date();
  const next = new Date();
  next.setUTCHours(11, 0, 0, 0); // 8 AM ART
  if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 1);
  const hours = Math.round((next.getTime() - now.getTime()) / (1000 * 60 * 60) * 10) / 10;
  const isToday = next.getUTCDate() === now.getUTCDate();
  return t(isToday ? 'users.onboarding_page.next_run_today' : 'users.onboarding_page.next_run_tomorrow', { hours });
}

export function UserOnboardingPage() {
  const { t } = useTranslation();
  const [estadoFilter, setEstadoFilter] = useState<'pendiente' | 'completada' | 'error' | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAlta, setSelectedAlta] = useState<OnboardingRow | null>(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  const { data: sheetData, isLoading, error, refetch } = trpc.users.getOnboardingStatus.useQuery();

  const triggerMutation = trpc.users.triggerAltas.useMutation({
    onSuccess: (result) => {
      const successMsg = t('users.toasts.onboarding_success', { count: result.altasCompletadas });
      const errorMsg = result.altasErrores > 0
        ? t('users.toasts.onboarding_with_errors', { errors: result.altasErrores })
        : '';
      toast.success(successMsg + errorMsg);
      refetch();
    },
    onError: (err) => toast.error(t('users.toasts.onboarding_error', { message: err.message })),
  });

  const resendMutation = trpc.users.resendOnboardingEmail.useMutation({
    onSuccess: (r) => {
      toast.success(t('users.onboarding_page.toasts.email_resent', { email: r.correo }));
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const groupMutation = trpc.users.addToSecurityGroupNow.useMutation({
    onSuccess: (r) => {
      toast.success(t('users.onboarding_page.toasts.group_added', { email: r.correo }));
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  // Grupo de seguridad, TODO el dominio (no solo las altas): quién sigue afuera.
  const securityAudit = trpc.users.securityGroupAudit.useQuery(undefined, { staleTime: 5 * 60_000 });
  const securityMissing = securityAudit.data?.missing ?? [];
  const [showSecurityDialog, setShowSecurityDialog] = useState(false);
  const bulkGroupMutation = trpc.users.addMissingToSecurityGroup.useMutation();
  // En tandas de 25: cada alta a Google tarda ~0,3 s y un solo request con
  // toda la lista chocaría con el techo de 30 s de API Gateway / CloudFront.
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const runBulkAdd = async () => {
    if (bulkProgress || securityMissing.length === 0) return;
    const emails = securityMissing.map((m) => m.email);
    const CHUNK = 25;
    let added = 0;
    let failed = 0;
    let skipped = 0;
    setBulkProgress({ done: 0, total: emails.length });
    try {
      for (let i = 0; i < emails.length; i += CHUNK) {
        const r = await bulkGroupMutation.mutateAsync({ emails: emails.slice(i, i + CHUNK) });
        added += r.added.length;
        failed += r.failed.length;
        skipped += r.skipped;
        setBulkProgress({ done: Math.min(i + CHUNK, emails.length), total: emails.length });
      }
      toast.success(
        t('users.onboarding_page.security_audit.toast_added', { added }) +
          (failed > 0 ? t('users.onboarding_page.security_audit.toast_failed', { failed }) : '') +
          (skipped > 0 ? t('users.onboarding_page.security_audit.toast_skipped', { skipped }) : '')
      );
      setShowSecurityDialog(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBulkProgress(null);
      securityAudit.refetch();
      refetch();
    }
  };

  const allAltas = useMemo(() => (sheetData || []) as unknown as OnboardingRow[], [sheetData]);

  const stats = {
    pendiente: allAltas.filter((a) => !a.correo_winclap).length,
    errores: allAltas.filter((a) => hasPipelineError(a.pipeline)).length,
    thisMonth: allAltas.filter((a: any) => {
      if (!a.fecha_ingreso_parsed) return false;
      const fecha = new Date(a.fecha_ingreso_parsed);
      const now = new Date();
      return fecha.getMonth() === now.getMonth() && fecha.getFullYear() === now.getFullYear();
    }).length,
  };

  const filteredAltas = allAltas.filter((alta) => {
    if (estadoFilter === 'pendiente' && alta.correo_winclap) return false;
    if (estadoFilter === 'completada' && !alta.correo_winclap) return false;
    if (estadoFilter === 'error' && !hasPipelineError(alta.pipeline)) return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        alta.nombre?.toLowerCase().includes(query) ||
        alta.correo_winclap?.toLowerCase().includes(query) ||
        alta.pais?.toLowerCase().includes(query) ||
        alta.area?.toLowerCase().includes(query) ||
        alta.id_alta?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Lista completa de la planilla montada de una: con cientos de filas
  // la página se hacía eterna.
  const { pageItems: pageAltas, footer: paginationFooter } = usePagination(filteredAltas, 25);

  const weekRange = useMemo(() => getCurrentWeekRangeAR(), []);
  const weekAltas = useMemo(
    () =>
      allAltas
        .filter((a: any) => isoDateInRange(a.fecha_ingreso_parsed, weekRange))
        .sort((a: any, b: any) => (a.fecha_ingreso_parsed || '').localeCompare(b.fecha_ingreso_parsed || '')),
    [allAltas, weekRange]
  );

  const handleResend = (id_alta: string, tipo: 'bienvenida' | 'ev') => resendMutation.mutate({ id_alta, tipo });
  const handleAddToGroup = (id_alta: string) => groupMutation.mutate({ id_alta });
  const actionPending = resendMutation.isPending || groupMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('users.onboarding_page.title')}</h2>
          <p className="text-muted-foreground">{t('users.onboarding_page.description')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowEmailPreview(true)}>
            <Mail className="mr-2 h-4 w-4" />
            {t('users.onboarding_page.preview.button')}
          </Button>
          <Button onClick={() => triggerMutation.mutate()} disabled={triggerMutation.isPending}>
            {triggerMutation.isPending ? (
              <WinclapLoader size={16} className="mr-2" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            {t('users.onboarding_page.process_pending')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="esta-semana">
        <TabsList>
          <TabsTrigger value="esta-semana">
            {t('users.week_tabs.this_week')}
            <span className="ml-1.5 rounded-full bg-muted-foreground/15 px-1.5 text-xs">{weekAltas.length}</span>
          </TabsTrigger>
          <TabsTrigger value="todos">{t('users.week_tabs.all')}</TabsTrigger>
        </TabsList>

        <TabsContent value="esta-semana" className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('users.week_tabs.week_of', {
              start: formatDayMonth(weekRange.weekStart),
              end: formatDayMonth(weekRange.weekEnd),
            })}
          </p>
          <Card>
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <WinclapLoader size={48} className="text-foreground/25" />
              </div>
            ) : weekAltas.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 space-y-2">
                <UserPlus className="h-12 w-12 text-muted-foreground" />
                <p className="font-semibold">{t('users.week_tabs.empty_week_altas')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('users.onboarding_page.table.join_date')}</TableHead>
                      <TableHead>{t('users.onboarding_page.table.name')}</TableHead>
                      <TableHead>{t('users.onboarding_page.table.winclap_email')}</TableHead>
                      <TableHead>{t('users.onboarding_page.table.country')}</TableHead>
                      <TableHead>{t('users.onboarding_page.table.community')}</TableHead>
                      <TableHead>{t('users.onboarding_page.table.area')}</TableHead>
                      <TableHead>{t('admin.useronboarding.equipo_asignado')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {weekAltas.map((alta) => (
                      <TableRow key={alta.rowNumber} className="cursor-pointer" onClick={() => setSelectedAlta(alta)}>
                        <TableCell className="text-sm whitespace-nowrap">{alta.fecha_ingreso || '-'}</TableCell>
                        <TableCell className="font-medium">{alta.nombre}</TableCell>
                        <TableCell className="text-sm font-mono">{alta.correo_winclap || '-'}</TableCell>
                        <TableCell className="text-sm">{alta.pais}</TableCell>
                        <TableCell className="text-sm">{alta.col_i || '—'}</TableCell>
                        <TableCell className="text-sm">{alta.area}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <AssignedDeviceCell correo={alta.correo_winclap} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="todos" className="mt-4 space-y-6">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('users.onboarding_page.stats.pending')}</p>
              <p className="text-2xl font-bold">{stats.pendiente}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('users.onboarding_page.stats.this_month')}</p>
              <p className="text-2xl font-bold">{stats.thisMonth}</p>
            </div>
            <UserPlus className="h-8 w-8 text-blue-500" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('users.onboarding_page.stats.errors')}</p>
              <p className="text-2xl font-bold">{stats.errores}</p>
            </div>
            {stats.errores > 0 ? (
              <AlertCircle className="h-8 w-8 text-red-500" />
            ) : (
              <CheckCircle className="h-8 w-8 text-green-500" />
            )}
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('users.onboarding_page.stats.next_run')}</p>
              <p className="text-lg font-bold">{nextRunLabel(t)}</p>
            </div>
            <CalendarClock className="h-8 w-8 text-violet-500" />
          </div>
        </Card>
      </div>

      {/* Error banner */}
      {stats.errores > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('users.onboarding_page.error_banner_title')}</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>{t('users.onboarding_page.error_banner', { count: stats.errores })}</span>
            <Button size="sm" variant="outline" onClick={() => setEstadoFilter('error')}>
              {t('users.onboarding_page.view_errors')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Grupo de seguridad: todo el dominio, no solo las altas */}
      {securityAudit.isLoading ? (
        <p className="text-xs text-muted-foreground">{t('users.onboarding_page.security_audit.loading')}</p>
      ) : securityAudit.error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('users.onboarding_page.security_audit.error', { message: securityAudit.error.message })}
          </AlertDescription>
        </Alert>
      ) : securityAudit.data && securityMissing.length > 0 ? (
        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>{t('users.onboarding_page.security_audit.title')}</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              {t('users.onboarding_page.security_audit.banner', {
                count: securityMissing.length,
                total: securityAudit.data.activeUsers,
                group: securityAudit.data.group,
              })}
            </span>
            <Button size="sm" variant="outline" onClick={() => setShowSecurityDialog(true)}>
              {t('users.onboarding_page.security_audit.view')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : securityAudit.data ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 text-green-500" />
          {t('users.onboarding_page.security_audit.ok', {
            count: securityAudit.data.activeUsers,
            group: securityAudit.data.group,
          })}
        </p>
      ) : null}

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('users.onboarding_page.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(!showFilters)}>
            <Filter className="mr-2 h-4 w-4" />
            {t('users.filters.filters')}
          </Button>
        </div>

        {showFilters && (
          <div className="grid gap-4 p-4 bg-muted/30 rounded-lg md:grid-cols-3">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('users.filters.status')}</label>
              <Select
                value={estadoFilter || 'all'}
                onValueChange={(v) => setEstadoFilter(v === 'all' ? undefined : (v as any))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('users.filters.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('users.filters.all')}</SelectItem>
                  <SelectItem value="pendiente">{t('users.filters.pending')}</SelectItem>
                  <SelectItem value="completada">{t('users.filters.completed')}</SelectItem>
                  <SelectItem value="error">{t('users.onboarding_page.filter_errors')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Card>

      {/* Data Table */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <WinclapLoader size={48} className="text-foreground/25" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <AlertCircle className="h-12 w-12 text-red-500" />
            <div className="text-center">
              <p className="font-semibold">{t('users.errors.load_data')}</p>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        ) : filteredAltas.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <UserPlus className="h-12 w-12 text-muted-foreground" />
            <div className="text-center">
              <p className="font-semibold">{t('users.onboarding_page.no_data')}</p>
              <p className="text-sm text-muted-foreground">{t('users.onboarding_page.data_realtime')}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex items-center justify-between px-4 pt-4">
              <p className="text-xs text-muted-foreground">{t('users.onboarding_page.legend_hint')}</p>
              <PipelineLegend />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('users.onboarding_page.table.join_date')}</TableHead>
                  <TableHead>{t('users.onboarding_page.table.name')}</TableHead>
                  <TableHead>{t('users.onboarding_page.table.winclap_email')}</TableHead>
                  <TableHead>{t('users.onboarding_page.table.password')}</TableHead>
                  <TableHead>{t('users.onboarding_page.table.country')}</TableHead>
                  <TableHead>{t('users.onboarding_page.table.area')}</TableHead>
                  <TableHead>{t('users.onboarding_page.table.pipeline')}</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageAltas.map((alta) => (
                  <TableRow
                    key={alta.rowNumber}
                    className="cursor-pointer"
                    onClick={() => setSelectedAlta(alta)}
                  >
                    <TableCell className="text-sm whitespace-nowrap">{alta.fecha_ingreso || '-'}</TableCell>
                    <TableCell>
                      <p className="font-medium">{alta.nombre}</p>
                      {alta.col_c && <p className="text-xs text-muted-foreground">{alta.col_c}</p>}
                    </TableCell>
                    <TableCell className="text-sm font-mono">{alta.correo_winclap || '-'}</TableCell>
                    <TableCell>
                      <PasswordCell password={alta.password} />
                    </TableCell>
                    <TableCell className="text-sm">{alta.pais}</TableCell>
                    <TableCell className="text-sm">{alta.area}</TableCell>
                    <TableCell>
                      <OnboardingPipeline pipeline={alta.pipeline} hasCorreo={!!alta.correo_winclap} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={t('common.a11y.more_actions')} className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setSelectedAlta(alta)}>
                            <Eye className="mr-2 h-4 w-4" /> {t('users.onboarding_page.actions.view')}
                          </DropdownMenuItem>
                          {alta.correo_winclap && (
                            <>
                              <DropdownMenuItem disabled={actionPending} onClick={() => handleResend(alta.id_alta, 'bienvenida')}>
                                <Mail className="mr-2 h-4 w-4" /> {t('users.onboarding_page.actions.resend_welcome')}
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={actionPending} onClick={() => handleResend(alta.id_alta, 'ev')}>
                                <Send className="mr-2 h-4 w-4" /> {t('users.onboarding_page.actions.resend_ev')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={actionPending || alta.pipeline?.security_group?.startsWith('agregado')}
                                onClick={() => handleAddToGroup(alta.id_alta)}
                              >
                                <ShieldCheck className="mr-2 h-4 w-4" /> {t('users.onboarding_page.actions.add_group_now')}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {paginationFooter && <div className="px-4 pb-3">{paginationFooter}</div>}
          </div>
        )}
      </Card>
        </TabsContent>
      </Tabs>

      <OnboardingDetailDialog
        alta={selectedAlta}
        onClose={() => setSelectedAlta(null)}
        onResendEmail={handleResend}
        onAddToGroup={handleAddToGroup}
        actionPending={actionPending}
      />

      <EmailPreviewDialog open={showEmailPreview} onClose={() => setShowEmailPreview(false)} />

      {/* Confirmación: sumar al grupo de seguridad a los activos que faltan */}
      <AlertDialog open={showSecurityDialog} onOpenChange={setShowSecurityDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.onboarding_page.security_audit.dialog_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('users.onboarding_page.security_audit.dialog_desc', { count: securityMissing.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ScrollArea className="max-h-64 rounded-md border p-2">
            <ul className="space-y-0.5 font-mono text-xs">
              {securityMissing.map((m) => (
                <li key={m.email} className="flex justify-between gap-2">
                  <span>{m.email}</span>
                  <span className="truncate text-muted-foreground">{m.orgUnitPath ?? ''}</span>
                </li>
              ))}
            </ul>
          </ScrollArea>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkProgress !== null}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkProgress !== null || securityMissing.length === 0}
              onClick={(e) => {
                e.preventDefault();
                void runBulkAdd();
              }}
            >
              {bulkProgress
                ? t('users.onboarding_page.security_audit.adding_progress', bulkProgress)
                : t('users.onboarding_page.security_audit.add_all', { count: securityMissing.length })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
